import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { usePermissions } from '../context/PermissionsContext';
import { useAuth } from '../context/AuthContext';
import { useViewAs } from '../context/ViewAsContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Map legacy permission keys to new admin_permissions keys
const LEGACY_TO_NEW_ADMIN_MAP = {
  'view_admin_console': 'admin_dashboard',
  'view_invoice_verification': 'admin_invoices',
  'verify_invoices': 'admin_invoices',
  'view_signature_documents': 'admin_signatures',
  'manage_signature_documents': 'admin_signatures',
  'manage_users': 'admin_users',
  'view_declinations_admin': 'admin_declinations',
  'manage_permission_levels': 'admin_permissions',
  'admin_only': 'admin_dashboard'
};

// Map legacy permission keys to nav_permissions keys
const LEGACY_TO_NAV_MAP = {
  'view_dashboard': 'dashboard',
  'view_reports': 'reports',
  'view_map': 'map',
  'view_personnel': 'personnel',
  'view_personnel_details': 'personnel',
  'view_agencies': 'agencies',
  'view_agency_details': 'agencies',
  'view_therapists': 'personnel',
  'upload_visits': 'uploads',
  'upload_projections': 'uploads',
  'upload_credentials': 'uploads',
  'upload_declinations': 'uploads',
  'upload_invoices': 'uploads',
  'manage_uploads': 'uploads'
};

/**
 * ProtectedRoute component that checks user permissions before rendering children
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - The component to render if authorized
 * @param {string} props.permission - Single permission required (optional)
 * @param {string[]} props.permissions - Array of permissions (optional)
 * @param {boolean} props.requireAll - If true, all permissions required; if false, any permission grants access
 * @param {boolean} props.adminOnly - If true, only admin users can access
 */
export default function ProtectedRoute({ 
  children, 
  permission, 
  permissions = [], 
  requireAll = false,
  adminOnly = false,
  selfService = false,
}) {
  const { hasPermission, hasAnyPermission, hasAllPermissions, isAdmin, loading: permissionsLoading } = usePermissions();
  const { user, loading: authLoading, isAuthenticated } = useAuth();
  const { viewingAs, effectivePermissions, isViewingAsOther } = useViewAs();
  const location = useLocation();
  const [userAdminPerms, setUserAdminPerms] = useState(null);
  const [userNavPerms, setUserNavPerms] = useState(null);
  const [loadingPerms, setLoadingPerms] = useState(true);

  // Determine effective user and permission level when viewing as another user
  const effectiveUser = isViewingAsOther ? viewingAs : user;
  const effectivePermissionLevelId = isViewingAsOther 
    ? effectivePermissions?.id 
    : user?.permission_level_id;

  // Fetch user's permission level to get admin_permissions and nav_permissions
  useEffect(() => {
    const fetchUserPermissions = async () => {
      console.log('ProtectedRoute: Fetching permissions for user:', effectiveUser?.email, 'permission_level_id:', effectivePermissionLevelId, 'isViewingAsOther:', isViewingAsOther);
      
      // If viewing as another user with effective permissions already loaded
      if (isViewingAsOther && effectivePermissions) {
        console.log('ProtectedRoute: Using effectivePermissions from ViewAs context:', effectivePermissions);
        setUserAdminPerms(effectivePermissions.admin_permissions || {});
        setUserNavPerms(effectivePermissions.nav_permissions || {});
        setLoadingPerms(false);
        return;
      }
      
      if (!effectivePermissionLevelId) {
        // No permission level - use role-based check
        // When viewing as, check the viewed user's role, not the admin's
        const roleToCheck = isViewingAsOther ? viewingAs?.role : user?.role;
        console.log('ProtectedRoute: No permission_level_id, using role-based check. Role:', roleToCheck);
        if (roleToCheck === 'admin') {
          setUserAdminPerms({
            admin_dashboard: true, admin_users: true, admin_permissions: true,
            admin_audit: true, admin_job_duties: true, admin_signatures: true,
            admin_declinations: true, admin_invoices: true, admin_integrations: true
          });
          setUserNavPerms({
            dashboard: true, personnel: true, agencies: true,
            reports: true, map: true, uploads: true
          });
        } else {
          setUserAdminPerms({});
          setUserNavPerms({});
        }
        setLoadingPerms(false);
        return;
      }
      
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(
          `${API}/permission-levels/${effectivePermissionLevelId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const level = response.data;
        console.log('ProtectedRoute: Fetched permission level:', level.name);
        console.log('ProtectedRoute: nav_permissions:', level.nav_permissions);
        console.log('ProtectedRoute: admin_permissions:', level.admin_permissions);
        setUserAdminPerms(level.admin_permissions || {});
        setUserNavPerms(level.nav_permissions || {});
      } catch (error) {
        console.error('ProtectedRoute: Failed to fetch permission level:', error);
        setUserAdminPerms({});
        setUserNavPerms({});
      }
      setLoadingPerms(false);
    };
    
    fetchUserPermissions();
  }, [user, isViewingAsOther, viewingAs, effectivePermissions, effectivePermissionLevelId]);

  // Wait for both auth and permissions to be fully loaded
  if (authLoading || permissionsLoading || loadingPerms) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  // If not authenticated, redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  // Check if user is admin (legacy check for users without permission_level_id)
  // When viewing as another user, use their role/permission level, not the admin's
  const roleToCheck = isViewingAsOther ? viewingAs?.role : user?.role;
  const permLevelId = isViewingAsOther ? effectivePermissions?.id : user?.permission_level_id;
  const userIsLegacyAdmin = (roleToCheck === 'admin') && !permLevelId && !isViewingAsOther;

  // Legacy admin always has access (but NOT when viewing as another user)
  if (userIsLegacyAdmin) {
    return children;
  }

  // Non-admins are limited to self-service pages (mirrors server-side access guard).
  if (roleToCheck !== 'admin' && !selfService) {
    return <Navigate to="/home" replace />;
  }

  // Check admin-only routes
  // Admin-only routes: role is the source of truth (mirrors server-side role check).
  if (adminOnly) {
    return children;
  }

  // Debug logging for permission checks
  console.log('ProtectedRoute: Checking access for path:', location.pathname);
  console.log('ProtectedRoute: Required permission:', permission, 'permissions array:', permissions);
  console.log('ProtectedRoute: userNavPerms:', userNavPerms);
  console.log('ProtectedRoute: userAdminPerms:', userAdminPerms);
  console.log('ProtectedRoute: isViewingAsOther:', isViewingAsOther);

  // Check nav_permissions first (for routes like /visits, /map, /personnel, /agencies)
  if (permission && userNavPerms) {
    const navPermKey = LEGACY_TO_NAV_MAP[permission];
    console.log('ProtectedRoute: Checking nav perm - legacy key:', permission, '-> nav key:', navPermKey, '-> value:', userNavPerms[navPermKey]);
    if (navPermKey && userNavPerms[navPermKey] === true) {
      console.log('ProtectedRoute: ACCESS GRANTED via nav_permissions');
      return children;
    }
  }

  // Check nav_permissions - multiple permissions
  if (permissions.length > 0 && userNavPerms) {
    const navPermKeys = permissions.map(p => LEGACY_TO_NAV_MAP[p]).filter(Boolean);
    console.log('ProtectedRoute: Checking multiple nav perms - keys:', navPermKeys);
    if (navPermKeys.length > 0) {
      const hasNavAccess = requireAll 
        ? navPermKeys.every(k => userNavPerms[k] === true)
        : navPermKeys.some(k => userNavPerms[k] === true);
      console.log('ProtectedRoute: hasNavAccess:', hasNavAccess);
      if (hasNavAccess) {
        console.log('ProtectedRoute: ACCESS GRANTED via nav_permissions (multiple)');
        return children;
      }
    }
  }

  // Check admin_permissions (for admin routes)
  if (permission && userAdminPerms) {
    const newPermKey = LEGACY_TO_NEW_ADMIN_MAP[permission];
    if (newPermKey && userAdminPerms[newPermKey] === true) {
      console.log('ProtectedRoute: ACCESS GRANTED via admin_permissions');
      return children;
    }
  }

  // Check admin_permissions - multiple permissions (hasAny by default)
  if (permissions.length > 0 && userAdminPerms) {
    const newPermKeys = permissions.map(p => LEGACY_TO_NEW_ADMIN_MAP[p]).filter(Boolean);
    if (newPermKeys.length > 0) {
      const hasNewAccess = requireAll 
        ? newPermKeys.every(k => userAdminPerms[k] === true)
        : newPermKeys.some(k => userAdminPerms[k] === true);
      if (hasNewAccess) {
        console.log('ProtectedRoute: ACCESS GRANTED via admin_permissions (multiple)');
        return children;
      }
    }
  }

  // Fallback to legacy permission check - single
  if (permission && hasPermission(permission)) {
    return children;
  }

  // Fallback to legacy - multiple permissions check
  if (permissions.length > 0) {
    const hasAccess = requireAll 
      ? hasAllPermissions(permissions) 
      : hasAnyPermission(permissions);
    
    if (hasAccess) {
      return children;
    }
  }

  // No access - redirect to first available page based on nav_permissions
  if (permission || permissions.length > 0) {
    // Determine the best redirect target based on user's permissions
    let redirectTo = '/dashboard';
    
    if (userNavPerms) {
      if (userNavPerms.dashboard === true) {
        redirectTo = '/dashboard';
      } else if (userNavPerms.reports === true || userNavPerms.map === true) {
        redirectTo = '/reports';
      } else if (userNavPerms.agencies === true) {
        redirectTo = '/agencies';
      } else if (userNavPerms.personnel === true) {
        redirectTo = '/personnel';
      } else if (userAdminPerms && Object.values(userAdminPerms).some(v => v === true)) {
        redirectTo = '/admin';
      }
    }
    
    console.log('ProtectedRoute: ACCESS DENIED, redirecting to:', redirectTo);
    return <Navigate to={redirectTo} replace state={{ from: location }} />;
  }

  return children;
}

/**
 * AccessDenied component - shown when user lacks permissions
 */
export function AccessDenied() {
  return (
    <div className="flex flex-col items-center justify-center min-h-[400px] text-center p-8">
      <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mb-6">
        <svg 
          xmlns="http://www.w3.org/2000/svg" 
          className="h-10 w-10 text-red-600" 
          fill="none" 
          viewBox="0 0 24 24" 
          stroke="currentColor"
        >
          <path 
            strokeLinecap="round" 
            strokeLinejoin="round" 
            strokeWidth={2} 
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" 
          />
        </svg>
      </div>
      <h2 className="text-2xl font-bold text-gray-800 mb-2">Access Denied</h2>
      <p className="text-gray-600 max-w-md">
        You don&apos;t have permission to view this page. Please contact your administrator 
        if you believe you should have access.
      </p>
    </div>
  );
}
